import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import client from "../lib/api";
import { Receipt, Sparkle, CircleNotch, X, Lightning } from "@phosphor-icons/react";

/**
 * InvoiceAutofillBanner.
 *
 * When the pilot arrives at `/app/invoices?load_id=X` from the
 * Ready-to-Invoice card, this banner pre-fetches the invoice draft
 * payload derived from the load (customer, rate, lane, due date, notes)
 * and lets them create the draft with ONE tap — no re-typing.
 *
 * On success:
 *   - Fires `onCreated?.(invoiceDoc)` so the parent CrudModule can reload
 *   - Clears the `?load_id=X` query param so a page reload doesn't
 *     re-render the banner for the same load
 *
 * Auto-hides when there's no `load_id` param or the draft fetch fails.
 */
export default function InvoiceAutofillBanner({ onCreated }) {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const loadId = params.get("load_id");

  const [draft, setDraft] = useState(null);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [autoSending, setAutoSending] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (!loadId || dismissed) return;
    let alive = true;
    setLoading(true);
    client.get(`/loads/${loadId}/invoice-draft`)
      .then((r) => alive && setDraft(r.data))
      .catch(() => alive && setDraft(null))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [loadId, dismissed]);

  const dismiss = () => {
    setDismissed(true);
    // Wipe the query param so a reload doesn't re-open the banner
    navigate("/app/invoices", { replace: true });
  };

  const createDraft = async () => {
    if (!draft) return;
    setCreating(true);
    try {
      const { data } = await client.post("/invoices", draft);
      toast.success(`Draft invoice ${data.invoice_number} created ✓`);
      onCreated?.(data);
      dismiss();
    } catch (e) {
      toast.error(
        e?.response?.data?.detail
          || "Could not create draft invoice",
      );
    } finally {
      setCreating(false);
    }
  };

  const createAndSend = async () => {
    if (!loadId) return;
    setAutoSending(true);
    try {
      const { data } = await client.post(
        `/loads/${loadId}/draft-and-send-invoice`,
        { attach_load_docs: true },
      );
      if (data.sent) {
        toast.success(
          `Invoice ${data.invoice?.invoice_number} sent to ${data.to} ✓`,
        );
      } else {
        toast.warning(
          `Draft created but email failed: ${data.send_error || "unknown"}`,
        );
      }
      onCreated?.(data.invoice);
      dismiss();
    } catch (e) {
      toast.error(
        e?.response?.data?.detail
          || "Could not draft + send. Check customer email is on file.",
      );
    } finally {
      setAutoSending(false);
    }
  };

  if (!loadId || dismissed) return null;
  if (loading) {
    return (
      <div
        data-testid="invoice-autofill-banner-loading"
        className="mx-4 sm:mx-8 mt-4 sm:mt-6 rounded-xl border border-emerald-500/25 bg-emerald-500/[0.03] px-5 py-3 text-sm text-neutral-400"
      >
        <CircleNotch size={14} className="inline animate-spin mr-2 text-emerald-400" />
        Pre-filling invoice draft…
      </div>
    );
  }
  if (!draft) return null;

  const money = "$" + Number(draft.amount || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  });

  return (
    <div
      data-testid="invoice-autofill-banner"
      className="mx-4 sm:mx-8 mt-4 sm:mt-6 rounded-xl border border-emerald-500/30 bg-gradient-to-br from-emerald-500/[0.08] to-transparent p-4 sm:p-5"
    >
      <div className="flex items-start gap-3">
        <div className="shrink-0 w-10 h-10 rounded-lg bg-emerald-500/20 text-emerald-300 flex items-center justify-center">
          <Sparkle size={18} weight="fill" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-[10px] uppercase tracking-widest text-emerald-400 font-bold">
            POD received · draft ready
          </div>
          <div className="font-display text-lg font-semibold text-white leading-tight mt-0.5">
            Create invoice{" "}
            <span data-testid="invoice-autofill-number" className="text-emerald-300 font-mono">
              {draft.invoice_number}
            </span>
            {" "}for{" "}
            <span data-testid="invoice-autofill-customer" className="text-white">
              {draft.customer || "customer"}
            </span>
          </div>
          <div className="text-xs text-neutral-400 mt-1 flex flex-wrap items-center gap-x-3 gap-y-1">
            <span>Amount: <b data-testid="invoice-autofill-amount" className="text-white">{money}</b></span>
            <span className="text-neutral-700">·</span>
            <span>Due: <b className="text-white">{draft.due_date}</b></span>
            {draft.load_reference && (
              <>
                <span className="text-neutral-700">·</span>
                <span>Load: <b className="text-white font-mono">{draft.load_reference}</b></span>
              </>
            )}
          </div>
          {draft.notes && (
            <div className="text-[11px] text-neutral-500 mt-1 italic truncate">
              {draft.notes}
            </div>
          )}
        </div>
        <div className="shrink-0 flex items-center gap-2">
          <button
            type="button"
            onClick={createAndSend}
            disabled={autoSending || creating || !draft.customer_email}
            data-testid="invoice-autofill-autosend-btn"
            title={draft.customer_email
              ? "Create the draft AND email it to the customer in one tap"
              : "No customer email on file — add one to enable auto-send"}
            className="inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-xs font-semibold bg-emerald-500 hover:bg-emerald-400 text-black disabled:opacity-50 transition-colors"
          >
            {autoSending ? (
              <><CircleNotch size={12} className="animate-spin" /> Sending…</>
            ) : (
              <><Lightning size={12} weight="fill" /> Draft + Send</>
            )}
          </button>
          <button
            type="button"
            onClick={createDraft}
            disabled={creating || autoSending}
            data-testid="invoice-autofill-create-btn"
            title="Create the draft only (review before sending)"
            className="inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-xs bg-white/[0.04] border border-white/10 text-neutral-300 hover:bg-white/[0.08] disabled:opacity-50 transition-colors"
          >
            {creating ? (
              <><CircleNotch size={12} className="animate-spin" /> Creating…</>
            ) : (
              <><Receipt size={12} weight="fill" /> Draft only</>
            )}
          </button>
          <button
            type="button"
            onClick={dismiss}
            data-testid="invoice-autofill-dismiss-btn"
            className="p-1.5 text-neutral-500 hover:text-white"
            aria-label="Dismiss"
          >
            <X size={14} />
          </button>
        </div>
      </div>
    </div>
  );
}
